import { Suspense, useMemo } from "react";
import ErrorBoundary from "lib/ErrorBoundary";
import LoadingContainer from "LoadingContainer";

import { useQuery } from "@apollo/client";
import { WorkspaceFinancialsDoc } from "operations";
import useToggleState from "useToggleState";
import { Brands } from "./Brands";
import { ColorCount } from "./ColorCount";
import { Currencies } from "./Currencies";
import { DeliveryWindow } from "./DeliveryWindow";
import { DepthCount } from "./DepthCount";
import { Financials } from "./Financials";
import { ProductCount } from "./ProductCount";
import { SkuCount } from "./SkuCount";
import { UnitCount } from "./UnitCount";
import { Retailers } from "./Retailers";
import {
  ButtonColumn,
  FinancialColumn,
  StyledSummaryBar,
  StyledTextButtonWrapper,
  StyledTextButton,
  SummaryBarContent,
  SummaryBarSection,
} from "./styledComponents";

const useShowAssortments = (id: string) => {
  const { data, loading } = useQuery(WorkspaceFinancialsDoc, {
    variables: { workspaceId: id },
  });

  const moreFinancials = useMemo(() => {
    const assortments = data?.retailWorkspace?.assortments;
    return assortments && assortments.length > 1;
  }, [data]);

  const getCurrencies = useMemo(() => {
    const brandCurrencies: string[] = [];
    const retailerCurrencies: string[] = [];

    data?.retailWorkspace?.assortments.flatMap((assortment) =>
      assortment.financialOverview.financialDetails.forEach((details) => {
        if (
          details.isBrandCurrency &&
          !brandCurrencies.includes(details.currencyCode)
        ) {
          brandCurrencies.push(details.currencyCode);
        }
        if (
          !details.isBrandCurrency &&
          !retailerCurrencies.includes(details.currencyCode)
        ) {
          retailerCurrencies.push(details.currencyCode);
        }
      })
    );
    return {
      brandCurrencies: brandCurrencies?.length || 0,
      retailerCurrencies: retailerCurrencies?.length || 0,
    };
  }, [data]);
  const { brandCurrencies, retailerCurrencies } = getCurrencies;
  return {
    loading,
    moreFinancials: moreFinancials,
    brandCurrencies: brandCurrencies,
    retailerCurrencies: retailerCurrencies,
  };
};

const SummaryBar = ({ workspaceId }: { workspaceId: string }) => {
  const [moreCurrencies, toggleMoreCurrencies] = useToggleState(false);
  const { moreFinancials, brandCurrencies, retailerCurrencies } =
    useShowAssortments(workspaceId);

  const showBrandCurrencies = moreCurrencies || retailerCurrencies === 0;

  return (
    <ErrorBoundary fallback={<LoadingSummaryBar />}>
      <Suspense fallback={<LoadingSummaryBar />}>
        <StyledSummaryBar>
          <SummaryBarSection>
            <SummaryBarContent>
              <FinancialColumn>
                {showBrandCurrencies && (
                  <Brands
                    workspaceId={workspaceId}
                    moreCurrencies={moreCurrencies}
                    numCurrencies={brandCurrencies}
                  />
                )}
                {retailerCurrencies > 0 && (
                  <Retailers workspaceId={workspaceId} />
                )}
              </FinancialColumn>
              <FinancialColumn>
                {showBrandCurrencies && (
                  <Currencies
                    workspaceId={workspaceId}
                    moreCurrencies={moreCurrencies}
                    isBrand
                  />
                )}
                {retailerCurrencies > 0 && (
                  <Currencies
                    workspaceId={workspaceId}
                    moreCurrencies={moreCurrencies}
                    isBrand={false}
                  />
                )}
              </FinancialColumn>
            </SummaryBarContent>
            <FinancialColumn>
              <Financials
                workspaceId={workspaceId}
                moreCurrencies={moreCurrencies}
                retailerFinancials={retailerCurrencies > 0}
                showBrandCurrencies={showBrandCurrencies}
              />
            </FinancialColumn>
            <FinancialColumn>
              <SummaryBarContent>
                <FinancialColumn>
                  {showBrandCurrencies && (
                    <ProductCount
                      workspaceId={workspaceId}
                      moreCurrencies={moreCurrencies}
                      isBrand
                    />
                  )}
                  {retailerCurrencies > 0 && (
                    <ProductCount
                      workspaceId={workspaceId}
                      moreCurrencies={moreCurrencies}
                      isBrand={false}
                    />
                  )}
                </FinancialColumn>
                <FinancialColumn>
                  {showBrandCurrencies && (
                    <ColorCount
                      workspaceId={workspaceId}
                      moreCurrencies={moreCurrencies}
                      isBrand
                    />
                  )}
                  {retailerCurrencies > 0 && (
                    <ColorCount
                      workspaceId={workspaceId}
                      moreCurrencies={moreCurrencies}
                      isBrand={false}
                    />
                  )}
                </FinancialColumn>
                <FinancialColumn>
                  {showBrandCurrencies && (
                    <SkuCount
                      workspaceId={workspaceId}
                      moreCurrencies={moreCurrencies}
                      isBrand
                    />
                  )}
                  {retailerCurrencies > 0 && (
                    <SkuCount
                      workspaceId={workspaceId}
                      moreCurrencies={moreCurrencies}
                      isBrand={false}
                    />
                  )}
                </FinancialColumn>
                <FinancialColumn>
                  {showBrandCurrencies && (
                    <UnitCount
                      workspaceId={workspaceId}
                      moreCurrencies={moreCurrencies}
                      isBrand
                    />
                  )}
                  {retailerCurrencies > 0 && (
                    <UnitCount
                      workspaceId={workspaceId}
                      moreCurrencies={moreCurrencies}
                      isBrand={false}
                    />
                  )}
                </FinancialColumn>
                <FinancialColumn>
                  {showBrandCurrencies && (
                    <DepthCount
                      workspaceId={workspaceId}
                      moreCurrencies={moreCurrencies}
                      isBrand
                    />
                  )}
                  {retailerCurrencies > 0 && (
                    <DepthCount
                      workspaceId={workspaceId}
                      moreCurrencies={moreCurrencies}
                      isBrand={false}
                    />
                  )}
                </FinancialColumn>
              </SummaryBarContent>
            </FinancialColumn>
            <DeliveryWindow workspaceId={workspaceId} />
          </SummaryBarSection>
          {moreFinancials && (
            <ButtonColumn>
              <StyledTextButtonWrapper>
                <StyledTextButton
                  iconName="expand"
                  onClick={toggleMoreCurrencies}
                >
                  {moreCurrencies ? "Less Currencies" : "More Currencies"}
                </StyledTextButton>
              </StyledTextButtonWrapper>
            </ButtonColumn>
          )}
        </StyledSummaryBar>
      </Suspense>
    </ErrorBoundary>
  );
};

const LoadingSummaryBar = () => (
  <StyledSummaryBar>
    <LoadingContainer loading />
  </StyledSummaryBar>
);

export default SummaryBar;
